import { Bell, X, CheckCircle, AlertCircle, MessageSquare, FileCheck, ExternalLink } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useNotifications } from '../../context/NotificationContext';

export function NotificationBadge({ onNotificationClick }) {
  const {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Filter notifications based on current filter
  const filteredNotifications = useMemo(() => {
    return filter === 'unread'
      ? notifications.filter(n => !n.isRead)
      : notifications;
  }, [notifications, filter]);

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'soap_note_assigned':
      case 'soap_note_reassigned':
      case 'soap_note_bulk_assigned':
        return <FileCheck className="w-5 h-5 text-blue-600" />;
      case 'soap_note_correction_completed':
      case 'soap_note_submitted_review':
        return <MessageSquare className="w-5 h-5 text-purple-600" />;
      case 'revision_requested':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'soap_note_approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'deadline_approaching':
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      case 'deadline_overdue':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  // Get background color based on notification type
  const getNotificationColor = (type) => {
    switch (type) {
      case 'soap_note_assigned':
      case 'soap_note_reassigned':
      case 'soap_note_bulk_assigned':
        return 'bg-blue-50 border border-blue-200 text-blue-800';
      case 'soap_note_correction_completed':
      case 'soap_note_submitted_review':
        return 'bg-purple-50 border border-purple-200 text-purple-800';
      case 'revision_requested':
        return 'bg-red-50 border border-red-200 text-red-800';
      case 'soap_note_approved':
        return 'bg-green-50 border border-green-200 text-green-800';
      case 'deadline_approaching':
        return 'bg-orange-50 border border-orange-200 text-orange-800';
      case 'deadline_overdue':
        return 'bg-red-50 border border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border border-gray-200 text-gray-800';
    }
  };

  // Format timestamp to relative time
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Handle click on a notification item
  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setIsOpen(false); // close the dropdown

    // Optionally mark as read immediately (could be done in modal)
    if (!notification.isRead) {
      markAsRead(notification.notificationId);
    }
  };

  // Handle "Go to SOAP Note" from modal
  const handleGoToSOAPNote = () => {
    if (!selectedNotification) return;
    
    // Mark as read if still unread
    if (!selectedNotification.isRead) {
      markAsRead(selectedNotification.notificationId);
    }
    
    // External navigation handler passed from parent
    if (onNotificationClick) {
      onNotificationClick(selectedNotification);
    }
    
    setSelectedNotification(null);
  };

  // Handle clearing a single notification
  const handleClear = (notificationId, e) => {
    e.stopPropagation();
    deleteNotification(notificationId);
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center border border-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {/* WebSocket connection indicator */}
        <div
          className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
            isConnected ? 'bg-green-500' : 'bg-gray-400'
          }`}
          title={isConnected ? 'Connected' : 'Disconnected'}
        />
      </button>

      {/* Notification Dropdown Panel */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          
          <div className={`
            fixed sm:absolute sm:right-0 sm:top-full sm:mt-2
            left-4 right-4 sm:left-auto sm:right-0
            w-auto sm:w-96
            max-h-[calc(100vh-2rem)] sm:max-h-[600px]
            bg-white border border-gray-200 rounded-xl shadow-lg z-50
            flex flex-col
            mx-auto sm:mx-0
          `}>
            {/* Header */}
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-lg text-gray-900">Notifications</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Filter Tabs */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                    filter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  All ({notifications.length})
                </button>
                <button
                  onClick={() => setFilter('unread')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                    filter === 'unread'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Unread ({unreadCount})
                </button>
              </div>

              {/* Mark All as Read */}
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
                >
                  Mark all as read
                </button>
              )}
            </div>

            {/* Notification List */}
            <div className="overflow-y-auto flex-1">
              {filteredNotifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">No notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredNotifications.map((notification) => (
                    <div
                      key={notification.notificationId}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        !notification.isRead ? 'bg-blue-50/50' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex gap-3">
                        {/* Icon */}
                        <div
                          className={`w-10 h-10 rounded-lg border flex items-center justify-center flex-shrink-0 ${getNotificationColor(
                            notification.type
                          )}`}
                        >
                          {getNotificationIcon(notification.type)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h4
                            className={`text-sm font-medium mb-1 ${
                              !notification.isRead ? 'text-gray-900' : 'text-gray-700'
                            }`}
                          >
                            {notification.title || 'Notification'}
                          </h4>
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                            {notification.message || ''}
                          </p>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500">
                              {formatTimestamp(notification.createdAt || notification.timestamp)}
                            </span>
                            <button
                              onClick={(e) => handleClear(notification.notificationId, e)}
                              className="text-xs text-gray-400 hover:text-gray-600 hover:underline"
                            >
                              Clear
                            </button>
                          </div>
                        </div>

                        {/* Unread dot (for visual emphasis) */}
                        {!notification.isRead && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full mt-2" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {filteredNotifications.length > 0 && (
              <div className="border-t border-gray-200 p-3 bg-gray-50 rounded-b-xl">
                <p className="text-xs text-gray-500 text-center">
                  {filteredNotifications.length} of {notifications.length} notifications shown
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Notification Detail Modal */}
      {selectedNotification && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black bg-opacity-50"
            onClick={() => setSelectedNotification(null)}
          />
          
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="border-b border-gray-200 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${getNotificationColor(selectedNotification.type)}`}
                  >
                    {getNotificationIcon(selectedNotification.type)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedNotification.title}</h3>
                    <p className="text-xs text-gray-500">
                      {formatTimestamp(selectedNotification.createdAt || selectedNotification.timestamp)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedNotification(null)}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Main Message */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Notification Message</h4>
                  <p className="text-gray-700">{selectedNotification.message}</p>
                </div>

                {/* Comments (if any) */}
                {selectedNotification.metadata?.comments && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Comments</h4>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-gray-700 whitespace-pre-line">
                        {selectedNotification.metadata.comments}
                      </p>
                    </div>
                  </div>
                )}

                {/* Notes (if any) */}
                {selectedNotification.metadata?.notes && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-blue-500 mb-2">Notes</h4>
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                      <p className="text-gray-700 whitespace-pre-line">
                        {selectedNotification.metadata.notes}
                      </p>
                    </div>
                  </div>
                )}

                {/* Metadata fields */}
                <div className="grid grid-cols-2 gap-4 text-sm mt-6">
                  {selectedNotification.metadata?.patientName && (
                    <div>
                      <span className="text-gray-500">Patient:</span>
                      <span className="ml-2 font-medium">{selectedNotification.metadata.patientName}</span>
                    </div>
                  )}
                  {selectedNotification.metadata?.sessionName && (
                    <div>
                      <span className="text-gray-500">Session:</span>
                      <span className="ml-2 font-medium">{selectedNotification.metadata.sessionName}</span>
                    </div>
                  )}
                  {selectedNotification.metadata?.deadline && (
                    <div>
                      <span className="text-gray-500">Deadline:</span>
                      <span className="ml-2 font-medium">{selectedNotification.metadata.deadline}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
                <button
                  onClick={() => setSelectedNotification(null)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Close
                </button>
                <button
                  onClick={handleGoToSOAPNote}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Go to SOAP Note
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}